import { useEffect, useMemo, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Users, UserCheck, Clock, ClipboardCheck, Search, ShieldCheck, ChevronDown, Mail, Building2, Landmark, FileText, UserPlus, XCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/motion";

const GROUPS = ["FASTSIGNS", "Independent", "Signarama", "SpeedPro"];

export default function AdminDealersPage() {
  const { dealer, checking } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [aliasInput, setAliasInput] = useState({});
  const [viewKind, setViewKind] = useState(null);
  const [subs, setSubs] = useState(null);
  const [expandedSub, setExpandedSub] = useState(null);
  const [mc, setMc] = useState(null);
  const [locs, setLocs] = useState(null);
  const [locQ, setLocQ] = useState("");
  const [editDealer, setEditDealer] = useState(null);
  const [editForm, setEditForm] = useState({ company: "", name: "", website: "", phone: "", group: "" });
  const [addrInput, setAddrInput] = useState({});
  const [siteInput, setSiteInput] = useState({});
  const [approveGroup, setApproveGroup] = useState({});

  const deleteLoc = async (l) => {
    if (!window.confirm(`Delete the map listing for ${l.company || "this pin"}${l.city ? ` (${l.city}${l.state ? `, ${l.state}` : ""})` : ""}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/dealer-locations/${l.id}`);
      toast.success("Map listing deleted", { description: l.company });
      setLocs(locs.filter((x) => x.id !== l.id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resyncMc = async () => {
    try {
      const r = await api.post("/admin/mailchimp-resync");
      toast.success(`MailChimp tags re-synced for ${r.data.synced} dealer accounts`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const SUB_KINDS = { dealer: "Dealer applications", corporate: "Corporate quotes", institutional: "Institutional quotes", contact: "Contact messages" };

  const toggleSubs = async (kind) => {
    if (viewKind === kind) { setViewKind(null); setSubs(null); setExpandedSub(null); return; }
    setViewKind(kind); setSubs(null); setExpandedSub(null);
    try {
      const r = await api.get(`/admin/submissions/${kind}`);
      setSubs(r.data);
    } catch {
      toast.error("Could not load submissions");
      setViewKind(null);
    }
  };

  const SUB_SKIP = new Set(["id", "created_at", "hubspot_sync", "email_notify", "name", "email", "company", "organization"]);
  const subDetails = (d) => Object.entries(d).filter(([k, v]) => !SUB_SKIP.has(k) && v !== null && v !== "" && v !== undefined);


  useEffect(() => {
    if (!checking && (!dealer || !dealer.is_admin)) navigate("/dealer-portal");
  }, [checking, dealer, navigate]);

  const load = () => {
    api.get("/admin/dealer-status")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load dealer status"));
    api.get("/admin/stats")
      .then((r) => setStats(r.data))
      .catch(() => {});
    api.get("/admin/dealer-applications")
      .then((r) => setApps(r.data))
      .catch(() => {});
    api.get("/admin/mailchimp-health")
      .then((r) => setMc(r.data))
      .catch(() => {});
    api.get("/admin/dealer-locations")
      .then((r) => setLocs(r.data.locations))
      .catch(() => {});
  };

  useEffect(() => {
    if (dealer?.is_admin) load();
  }, [dealer]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.dealers.filter((d) => {
      if (filter === "awaiting" && d.activated) return false;
      if (filter === "activated" && !d.activated) return false;
      if (filter === "no-website" && (d.website || "").trim()) return false;
      if (GROUPS.includes(filter) && d.group !== filter) return false;
      if (needle && ![d.name, d.company, d.email, ...d.aliases].join(" ").toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [data, filter, q]);

  if (checking || !dealer?.is_admin) return <div className="min-h-screen" />;

  const confirmed = data ? data.dealers.filter((d) => d.profile_completed).length : 0;

  const toggleDirectory = async (d) => {
    const next = !d.directory.visible;
    try {
      await api.patch(`/admin/dealers/${encodeURIComponent(d.email)}/directory-visibility`, { visible: next });
      setData({ ...data, dealers: data.dealers.map((x) => x.email === d.email ? { ...x, directory: { ...x.directory, visible: next } } : x) });
      toast.success(next ? "Shown in dealer directory" : "Hidden from dealer directory", { description: d.company || d.email });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const normName = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const emailDomain = (e) => (e || "").split("@")[1] || "";

  const FREE_DOMAINS = ["gmail.com", "yahoo.com", "aol.com", "hotmail.com", "me.com", "outlook.com"];

  const strongMatches = (app) => {
    // Reliable suggestions only: exact company-name match, or same domain + same person name
    if (!data) return [];
    const appCo = normName(app.company);
    const appName = normName(app.name);
    const appDom = emailDomain(app.email);
    return data.dealers.filter((d) => {
      if (appCo && normName(d.company) === appCo) return true;
      if (appDom && !FREE_DOMAINS.includes(appDom) && appName && normName(d.name) === appName &&
          [d.email, ...(d.aliases || [])].some((e) => emailDomain(e) === appDom)) return true;
      return false;
    });
  };

  const weakDomainCount = (app) => {
    if (!data) return 0;
    const appDom = emailDomain(app.email);
    if (!appDom || FREE_DOMAINS.includes(appDom)) return 0;
    return data.dealers.filter((d) => [d.email, ...(d.aliases || [])].some((e) => emailDomain(e) === appDom)).length;
  };

  const suggestGroup = (a) => {
    const t = `${a.company || ""} ${a.email || ""}`.toLowerCase();
    if (t.includes("fastsigns")) return "FASTSIGNS";
    if (t.includes("signarama")) return "Signarama";
    if (t.includes("speedpro")) return "SpeedPro";
    return "Independent";
  };

  const approveApp = async (app, linkEmail) => {
    try {
      const payload = linkEmail
        ? { link_to_email: linkEmail, send_setup_email: true }
        : { group: approveGroup[app.id] ?? suggestGroup(app), send_setup_email: true };
      const r = await api.post(`/admin/dealer-applications/${app.id}/approve`, payload);
      toast.success(linkEmail ? `Added as contact on ${r.data.linked_to}` : `Dealer account created for ${r.data.created}`, { description: "Setup email sent" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const dismissApp = async (app) => {
    try {
      await api.post(`/admin/dealer-applications/${app.id}/dismiss`);
      toast.success("Application dismissed", { description: app.company || app.email });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const transferApp = async (app, accountEmail) => {
    if (!window.confirm(`Transfer the ${accountEmail} account to ${app.name} (${app.email})?\n\nThe account's contact info updates from this application, the old contact's login stops working, and ${app.email} gets a fresh setup email.`)) return;
    try {
      const r = await api.post(`/admin/dealer-applications/${app.id}/transfer`, { account_email: accountEmail });
      toast.success(`Account transferred to ${r.data.transferred_to}`, { description: "Setup email sent — old contact no longer has access" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addAlias = async (d, alias) => {
    try {
      await api.post(`/admin/dealers/${encodeURIComponent(d.email)}/aliases`, { alias });
      toast.success(`Contact added: ${alias}`, { description: d.company });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const mergeDuplicate = async (keeper, dup) => {
    const fmt = (x) => `  contact: ${x.name || "—"}\n  phone: ${x.phone || "—"}\n  website: ${x.website || "—"}\n  status: ${x.activated ? "Active (has password)" : "Invited"}`;
    if (!window.confirm(
      `Merge these two accounts?\n\n` +
      `STAYS (keeper) — ${keeper.email}\n${fmt(keeper)}\n\n` +
      `ARCHIVED — ${dup.email}\n${fmt(dup)}\n\n` +
      `What happens:\n` +
      `- ${[dup.email, ...(dup.aliases || [])].join(", ")} move(s) onto the keeper as contact login(s)\n` +
      `- The keeper's profile above is the one that's kept\n` +
      `- The archived account's login stops working and it leaves the tracker\n` +
      (dup.directory?.listed ? `- The archived account's map pin is removed; the keeper's pin stays\n` : "") +
      `\nThis can't be undone from the admin panel.`
    )) return;
    try {
      const r = await api.post(`/admin/dealers/${encodeURIComponent(keeper.email)}/merge`, { duplicate_email: dup.email });
      toast.success(`Merged ${r.data.merged} into ${r.data.into}`, { description: `${r.data.contacts_moved} contact(s) moved` });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeAlias = async (d, alias) => {
    try {
      await api.delete(`/admin/dealers/${encodeURIComponent(d.email)}/aliases/${encodeURIComponent(alias)}`);
      toast.success(`Contact removed: ${alias}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendSetupLink = async (email) => {
    try {
      await api.post("/admin/dealers/resend-setup", { email });
      toast.success("Setup email sent", { description: `${email} — your welcoming version` });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteSub = async (d) => {
    if (!window.confirm(`Delete this submission from ${d.name || d.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/submissions/${viewKind}/${d.id}`);
      toast.success("Submission deleted");
      setSubs(subs.filter((x) => x.id !== d.id));
      setExpandedSub(null);
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteDealer = async (d) => {
    const contacts = d.aliases.length ? ` plus ${d.aliases.length} extra contact${d.aliases.length === 1 ? "" : "s"}` : "";
    if (!window.confirm(`Permanently delete the account for ${d.company || d.email} (${d.email}${contacts})? Their dealer-map pin is removed too. This cannot be undone.`)) return;
    try {
      const r = await api.delete(`/admin/dealers/${encodeURIComponent(d.email)}`);
      toast.success("Dealer account deleted", { description: r.data.removed_locations ? `${d.email} — removed ${r.data.removed_locations} map pin${r.data.removed_locations === 1 ? "" : "s"}` : d.email });
      setExpanded(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveDealerEdit = async (d) => {
    try {
      const r = await api.patch(`/admin/dealers/${encodeURIComponent(d.email)}`, { company: editForm.company, name: editForm.name, website: editForm.website, phone: editForm.phone, group: editForm.group || undefined });
      toast.success("Account updated", { description: r.data.directory_note || editForm.company || d.email });
      setEditDealer(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const createListing = async (d) => {
    const address = (addrInput[d.email] || "").trim();
    const website = (siteInput[d.email] || "").trim();
    if (address.length < 10) { toast.error("Enter the full street address, city, state and ZIP"); return; }
    if (!d.website && !website) { toast.error("Add their website too — it's required for the map listing"); return; }
    try {
      const payload = { shipping_address: address };
      if (website) payload.website = website;
      const r = await api.patch(`/admin/dealers/${encodeURIComponent(d.email)}`, payload);
      toast.success("Listing saved", { description: r.data.directory_note || "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };


  const chip = (key, label) => (
    <button key={key} onClick={() => setFilter(key)} data-testid={`filter-${key.toLowerCase().replace(/\s+/g, "-")}`}
      className={`clip-corner-sm px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors ${filter === key ? "bg-hivis text-obsidian" : "border border-girder text-slate-400 hover:border-hivis/60 hover:text-hivis"}`}>
      {label}
    </button>
  );

  return (
    <div data-testid="admin-dealers-page">
      <PageHeader
        kicker="Owner Tools"
        title="Dealer activation tracker"
        sub="Who has set their password, who has confirmed their details, and who needs a nudge."
        image="/assets/img/w-gallery-img-5882.jpg"
      />
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-12 lg:py-16">
        {!data ? (
          <p className="font-mono text-sm uppercase tracking-widest text-slate-500">Loading dealer status...</p>
        ) : (
          <>
            <Reveal>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="tracker-stats">
                {[
                  { icon: Users, label: "Dealer accounts", value: data.total },
                  { icon: UserCheck, label: "Activated", value: data.activated },
                  { icon: Clock, label: "Awaiting setup", value: data.total - data.activated },
                  { icon: ClipboardCheck, label: "Details confirmed", value: confirmed },
                ].map((s) => (
                  <div key={s.label} className="clip-corner border border-girder bg-steel/40 p-5">
                    <s.icon className="h-5 w-5 text-hivis" />
                    <p className="mt-3 font-display text-3xl font-black text-slate-100">{s.value}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{s.label}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            {apps && apps.applications.filter((a) => a.status === "pending").length > 0 && (
              <Reveal i={1}>
                <div className="mt-8 clip-corner border border-hivis/40 bg-obsidian/60" data-testid="dealer-applications">
                  <p className="border-b border-girder bg-steel/60 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-hivis">
                    Pending dealer applications ({apps.applications.filter((a) => a.status === "pending").length})
                  </p>
                  <div className="divide-y divide-girder/50">
                    {apps.applications.filter((a) => a.status === "pending").map((a) => {
                      const matches = strongMatches(a);
                      const weak = weakDomainCount(a) - matches.length;
                      return (
                        <div key={a.id} className="px-4 py-4" data-testid={`application-row-${a.id}`}>
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <p className="font-display text-lg font-bold uppercase text-slate-100">{a.company || a.name}</p>
                              <p className="mt-0.5 text-sm text-slate-400">
                                {a.name} · {a.email}{a.phone ? ` · ${a.phone}` : ""}{a.business_type ? ` · ${a.business_type}` : ""}
                              </p>
                              {a.message && <p className="mt-1 text-xs italic text-slate-500">"{a.message}"</p>}
                              {matches.length > 0 && (
                                <p className="mt-2 text-xs text-amber-300/90">
                                  Possible existing dealer: {matches.map((m) => `${m.company || m.email} (${m.email})`).join(", ")}
                                </p>
                              )}
                              {matches.length === 0 && weak > 0 && (
                                <p className="mt-2 text-xs text-amber-300/90">
                                  {weak} account{weak === 1 ? "" : "s"} share the {emailDomain(a.email)} domain — find the right store in the tracker below, expand its row, and add this contact there.
                                </p>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              {matches.slice(0, 2).map((m) => (
                                <Fragment key={m.email}>
                                  <button onClick={() => approveApp(a, m.email)} data-testid={`approve-link-${a.id}-${m.email}`}
                                    className="clip-corner-sm border border-hivis/50 px-3 py-2 font-display text-xs font-bold uppercase text-hivis transition-colors hover:bg-hivis hover:text-obsidian">
                                    Add as contact
                                  </button>
                                  <button onClick={() => transferApp(a, m.email)} data-testid={`approve-transfer-${a.id}-${m.email}`}
                                    className="clip-corner-sm border border-red-400/60 px-3 py-2 font-display text-xs font-bold uppercase text-red-300 transition-colors hover:bg-red-400 hover:text-obsidian">
                                    Transfer account
                                  </button>
                                </Fragment>
                              ))}
                              <select
                                value={approveGroup[a.id] ?? suggestGroup(a)}
                                onChange={(e) => setApproveGroup({ ...approveGroup, [a.id]: e.target.value })}
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`approve-group-${a.id}`}
                                title="Franchise group — auto-suggested, change if needed"
                                className="border border-girder bg-obsidian px-2 py-2 font-mono text-[11px] uppercase tracking-widest text-slate-300 focus:border-hivis focus:outline-none">
                                {GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
                              </select>
                              <button onClick={() => approveApp(a, null)} data-testid={`approve-create-${a.id}`}
                                className="clip-corner-sm bg-hivis px-3 py-2 font-display text-xs font-bold uppercase text-obsidian transition-colors hover:bg-hivis-hover">
                                {matches.length > 0 ? "Create new account" : "Approve"}
                              </button>
                              <button onClick={() => dismissApp(a)} data-testid={`dismiss-${a.id}`}
                                className="px-3 py-2 font-display text-xs font-bold uppercase text-slate-500 transition-colors hover:text-red-400">
                                Dismiss
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Reveal>
            )}

            {stats && (
              <>
                <Reveal i={1}>
                  <div className="mt-8">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-hivis">Site activity — form submissions</p>
                    <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="submission-stats">
                      {[
                        { icon: FileText, label: "Dealer applications", value: stats.dealer_applications, kind: "dealer" },
                        { icon: Building2, label: "Corporate quotes", value: stats.corporate_quotes, kind: "corporate" },
                        { icon: Landmark, label: "Institutional quotes", value: stats.institutional_quotes, kind: "institutional" },
                        { icon: Mail, label: "Contact messages", value: stats.contact_messages, kind: "contact" },
                      ].map((s) => (
                        <button key={s.label} onClick={() => toggleSubs(s.kind)} data-testid={`view-${s.kind}-submissions`}
                          className={`clip-corner flex items-center gap-4 border p-4 text-left transition-colors ${viewKind === s.kind ? "border-hivis bg-steel/60" : "border-girder bg-obsidian/60 hover:border-slate-500"}`}>
                          <s.icon className="h-5 w-5 shrink-0 text-hivis" />
                          <div>
                            <p className="font-display text-2xl font-black leading-none text-slate-100">{s.value}</p>
                            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{s.label}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                    {mc && (
                      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="mailchimp-status">
                        <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">
                          MailChimp audience: {mc.ok ? <span className="text-hivis">{mc.audience_name} — {mc.members} members</span> : mc.configured ? <span className="text-red-400">connection problem{mc.status ? ` (HTTP ${mc.status})` : ""}</span> : <span className="text-red-400">not configured</span>}
                        </span>
                        <button onClick={resyncMc} data-testid="mailchimp-resync"
                          className="clip-corner-sm border border-girder px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-300 transition-colors hover:border-hivis hover:text-hivis">
                          Re-sync dealer tags
                        </button>
                      </div>
                    )}
                  </div>
                </Reveal>
                <Reveal i={2}>
                  {viewKind ? (
                  <div className="mt-4 clip-corner border border-hivis/40" data-testid="submissions-panel">
                    <div className="flex items-center justify-between border-b border-girder bg-steel/60 px-4 py-3">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-hivis">{SUB_KINDS[viewKind]}{subs ? ` — ${subs.length}` : ""}</p>
                      <button onClick={() => toggleSubs(viewKind)} data-testid="submissions-close" className="font-mono text-[10px] uppercase tracking-widest text-slate-500 transition-colors hover:text-hivis">Close ✕</button>
                    </div>
                    <ul className="divide-y divide-girder/50">
                      {!subs && <li className="px-4 py-6 text-center font-mono text-xs uppercase tracking-widest text-slate-600">Loading...</li>}
                      {subs && subs.length === 0 && (
                        <li className="px-4 py-6 text-center font-mono text-xs uppercase tracking-widest text-slate-600">No submissions yet</li>
                      )}
                      {subs && subs.map((d, i) => (
                        <li key={d.id || i}>
                          <div onClick={() => setExpandedSub(expandedSub === i ? null : i)} data-testid={`submission-row-${i}`}
                            className="flex w-full cursor-pointer flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-left text-sm transition-colors hover:bg-steel/40">
                            <span className="font-bold text-slate-100">{d.company || d.organization || d.name || "—"}</span>
                            <span className="text-xs text-slate-500">{d.name}{d.email ? ` · ${d.email}` : ""}</span>
                            {d.status && <span className="clip-corner-sm border border-hivis/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-hivis">{d.status}</span>}
                            {d.email_notify && (
                              <span data-testid={`submission-email-status-${i}`} title="Owner notification email delivery"
                                className={`clip-corner-sm border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${d.email_notify === "sent" ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400"}`}>
                                Email {d.email_notify}
                              </span>
                            )}
                            <span className="ml-auto font-mono text-[11px] text-slate-600">{d.created_at ? new Date(d.created_at).toLocaleString() : ""}</span>
                            <button onClick={(e) => { e.stopPropagation(); deleteSub(d); }} data-testid={`delete-submission-${i}`} aria-label="Delete submission"
                              className="text-slate-600 transition-colors hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                          {expandedSub === i && (
                            <dl className="border-t border-girder/50 bg-obsidian/40 px-4 py-3" data-testid={`submission-detail-${i}`}>
                              {subDetails(d).map(([k, v]) => (
                                <div key={k} className="grid grid-cols-[120px_1fr] gap-3 py-1 sm:grid-cols-[180px_1fr]">
                                  <dt className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{k.replace(/_/g, " ")}</dt>
                                  <dd className="whitespace-pre-wrap text-sm text-slate-300">{String(v)}</dd>
                                </div>
                              ))}
                            </dl>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                  ) : (
                  <div className="mt-4 clip-corner border border-girder" data-testid="recent-submissions">
                    <p className="border-b border-girder bg-steel/60 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Latest submissions</p>
                    <ul className="divide-y divide-girder/50">
                      {stats.recent.length === 0 && (
                        <li className="px-4 py-6 text-center font-mono text-xs uppercase tracking-widest text-slate-600">No submissions yet</li>
                      )}
                      {stats.recent.map((r, i) => (
                        <li key={i} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm" data-testid={`recent-submission-${i}`}>
                          <span className="clip-corner-sm border border-hivis/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-hivis">{r.kind}</span>
                          <span className="font-bold text-slate-100">{r.company || r.name || "—"}</span>
                          <span className="text-xs text-slate-500">{r.name}{r.email ? ` · ${r.email}` : ""}</span>
                          <span className="ml-auto font-mono text-[11px] text-slate-600">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  )}
                </Reveal>
              </>
            )}

            {locs && locs.filter((l) => !l.user_id).length > 0 && (
              <Reveal i={2}>
                <div className="mt-4 clip-corner border border-girder" data-testid="map-listings">
                  <div className="flex flex-wrap items-center gap-3 border-b border-girder bg-steel/60 px-4 py-3">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                      Map listings without a dealer account ({locs.filter((l) => !l.user_id).length}) — these pins came from the address import; delete duplicates here
                    </p>
                    <div className="relative ml-auto w-full sm:w-64">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                      <input value={locQ} onChange={(e) => setLocQ(e.target.value)} placeholder="Search company or city..."
                        data-testid="map-listings-search"
                        className="w-full border border-girder bg-obsidian py-2 pl-9 pr-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-hivis focus:outline-none" />
                    </div>
                  </div>
                  <ul className="max-h-96 divide-y divide-girder/50 overflow-y-auto">
                    {locs.filter((l) => !l.user_id)
                      .filter((l) => { const n = locQ.trim().toLowerCase(); return !n || [l.company, l.city, l.state].filter(Boolean).join(" ").toLowerCase().includes(n); })
                      .map((l) => (
                      <li key={l.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm" data-testid={`map-listing-${l.id}`}>
                        <span className="font-bold text-slate-100">{l.company || "—"}</span>
                        <span className="text-xs text-slate-500">{[l.city, l.state].filter(Boolean).join(", ") || "—"}</span>
                        {!l.active && <span className="font-mono text-[10px] uppercase tracking-widest text-slate-600">hidden</span>}
                        <button onClick={() => deleteLoc(l)} data-testid={`delete-listing-${l.id}`} aria-label={`Delete map listing ${l.company}`}
                          className="ml-auto text-slate-600 transition-colors hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                      </li>
                    ))}
                    {locs.filter((l) => !l.user_id).filter((l) => { const n = locQ.trim().toLowerCase(); return !n || [l.company, l.city, l.state].filter(Boolean).join(" ").toLowerCase().includes(n); }).length === 0 && (
                      <li className="px-4 py-6 text-center font-mono text-xs uppercase tracking-widest text-slate-600">No listings match</li>
                    )}
                  </ul>
                </div>
              </Reveal>
            )}

            <Reveal i={3}>
              <div className="mt-8 flex flex-wrap items-center gap-2">
                {chip("all", "All")}
                {chip("awaiting", "Awaiting setup")}
                {chip("activated", "Activated")}
                {chip("no-website", "No website")}
                <span className="mx-2 hidden h-5 w-px bg-girder sm:block" />
                {GROUPS.map((g) => chip(g, g))}
                <div className="relative ml-auto w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, company, email..."
                    data-testid="tracker-search"
                    className="w-full border border-girder bg-obsidian py-2.5 pl-9 pr-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-hivis focus:outline-none" />
                </div>
              </div>
            </Reveal>

            <Reveal i={2}>
              <div className="mt-6 overflow-x-auto clip-corner border border-girder" data-testid="dealer-status-table">
                <table className="w-full min-w-[900px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-girder bg-steel/60 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                      <th className="px-4 py-3">Status</th><th className="px-4 py-3">Dealer</th><th className="px-4 py-3">Email logins</th>
                      <th className="px-4 py-3">Group</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Activated</th>
                      <th className="px-4 py-3">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((d) => (
                      <Fragment key={d.email}>
                      <tr onClick={() => setExpanded(expanded === d.email ? null : d.email)} className="cursor-pointer border-b border-girder/50 transition-colors hover:bg-steel/30" data-testid={`dealer-row-${d.email}`}>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest ${d.activated ? "text-hivis" : "text-slate-500"}`}>
                            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded === d.email ? "rotate-180" : ""}`} />
                            <span className={`h-2 w-2 rounded-full ${d.activated ? "bg-hivis" : "bg-slate-600"}`} />
                            {d.activated ? "Active" : "Invited"}
                          </span>
                          {!d.activated && (
                            <>
                              {d.setup_sent > 0 && (
                                <span className="ml-6 block font-mono text-[10px] uppercase tracking-widest text-slate-500" data-testid={`setup-count-${d.email}`}>
                                  Setup emails sent: {d.setup_sent}{d.last_setup_at ? ` · last ${new Date(d.last_setup_at).toLocaleDateString()}` : ""}
                                </span>
                              )}
                              <button onClick={(e) => { e.stopPropagation(); sendSetupLink(d.email); }} data-testid={`resend-setup-${d.email}`}
                                className="ml-6 block whitespace-nowrap font-mono text-[10px] uppercase tracking-widest text-hivis hover:underline">
                                Resend setup email
                              </button>
                            </>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-bold text-slate-100">{d.company || "—"}</p>
                          {d.name && <p className="text-xs text-slate-500">{d.name}</p>}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-400">
                          {d.email}
                          {d.aliases.length > 0 && <span className="ml-1 text-slate-600">(+{d.aliases.length} more)</span>}
                        </td>
                        <td className="px-4 py-3">
                          {d.group && <span className="clip-corner-sm border border-girder px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-400">{d.group}</span>}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-400">{d.phone || "—"}</td>
                        <td className="px-4 py-3 font-mono text-[11px] text-slate-500">{d.activated_at ? new Date(d.activated_at).toLocaleDateString() : "—"}</td>
                        <td className="px-4 py-3">
                          {d.profile_completed
                            ? <ShieldCheck className="h-4 w-4 text-hivis" aria-label="Details confirmed" />
                            : <span className="font-mono text-[11px] text-slate-600">—</span>}
                        </td>
                      </tr>
                      {expanded === d.email && (
                        <tr className="border-b border-girder/50 bg-steel/20" data-testid={`dealer-detail-${d.email}`}>
                          <td colSpan={7} className="px-4 py-4">
                            <div className="grid gap-4 text-sm sm:grid-cols-3">
                              <div><p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Shipping address</p><p className="mt-1 whitespace-pre-line text-slate-300">{d.shipping_address || "—"}</p></div>
                              <div><p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Preferred contact</p><p className="mt-1 text-slate-300">{d.preferred_contact || "—"}</p></div>
                              <div><p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Website</p><p className="mt-1 text-slate-300">{d.website ? <a href={d.website.startsWith("http") ? d.website : `https://${d.website}`} target="_blank" rel="noreferrer" className="text-hivis underline underline-offset-2">{d.website}</a> : "—"}</p></div>
                              <div><p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Dealer notes</p><p className="mt-1 whitespace-pre-line text-slate-300">{d.notes || "—"}</p></div>
                              <div className="md:col-span-2">
                                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Contacts on this account</p>
                                <div className="mt-1 space-y-1.5">
                                  {[d.email, ...(d.aliases || [])].map((em) => (
                                    <div key={em} className="flex flex-wrap items-center gap-2 text-sm">
                                      <span className="text-slate-300">{em}{em === d.email && <span className="ml-1 font-mono text-[10px] uppercase text-slate-600">(primary)</span>}</span>
                                      <button onClick={(e) => { e.stopPropagation(); sendSetupLink(em); }} data-testid={`send-setup-${em}`}
                                        className="font-mono text-[10px] uppercase tracking-widest text-hivis hover:underline">Send setup link</button>
                                      {em !== d.email && (
                                        <button onClick={(e) => { e.stopPropagation(); removeAlias(d, em); }} data-testid={`remove-alias-${em}`}
                                          className="text-slate-600 transition-colors hover:text-red-400" aria-label={`Remove ${em}`}><XCircle className="h-3.5 w-3.5" /></button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                <div className="mt-2 flex items-center gap-2">
                                  <input
                                    value={aliasInput[d.email] || ""}
                                    onChange={(e) => setAliasInput({ ...aliasInput, [d.email]: e.target.value })}
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="Add contact email"
                                    data-testid={`alias-input-${d.email}`}
                                    className="w-64 border border-girder bg-obsidian px-3 py-1.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-hivis focus:outline-none"
                                  />
                                  <button
                                    onClick={(e) => { e.stopPropagation(); if ((aliasInput[d.email] || "").includes("@")) addAlias(d, aliasInput[d.email].trim()); }}
                                    data-testid={`add-alias-${d.email}`}
                                    className="clip-corner-sm border border-girder px-3 py-1.5 font-display text-xs font-bold uppercase text-slate-200 transition-colors hover:border-hivis hover:text-hivis">
                                    <UserPlus className="mr-1 inline h-3.5 w-3.5" /> Add contact
                                  </button>
                                </div>
                              </div>
                              {(() => {
                                const coD = normName(d.company);
                                const doms = [d.email, ...(d.aliases || [])].map((e) => emailDomain(e));
                                const related = (apps?.applications || []).filter((p) => {
                                  if (p.status !== "pending") return false;
                                  const coP = normName(p.company);
                                  if (coD && coP && (coD.includes(coP) || coP.includes(coD))) return true;
                                  const pDom = emailDomain(p.email);
                                  return pDom && !FREE_DOMAINS.includes(pDom) && doms.includes(pDom);
                                });
                                if (!related.length) return null;
                                return (
                                  <div className="md:col-span-3 border-t border-girder/50 pt-3" data-testid={`related-apps-${d.email}`}>
                                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Pending applications that may belong to this store</p>
                                    {related.map((p) => (
                                      <div key={p.id} className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                                        <span className="text-slate-300">{p.company || p.name} — {p.name} · {p.email}</span>
                                        <button onClick={(e) => { e.stopPropagation(); transferApp(p, d.email); }} data-testid={`transfer-to-applicant-${p.id}`}
                                          className="clip-corner-sm border border-red-400/60 px-2.5 py-1 font-display text-[11px] font-bold uppercase text-red-300 transition-colors hover:bg-red-400 hover:text-obsidian">
                                          Transfer account to {p.name}
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}
                              {(() => {
                                const co = normName(d.company);
                                if (!co) return null;
                                const dups = (data?.dealers || []).filter((o) => o.email !== d.email && normName(o.company) === co);
                                if (!dups.length) return null;
                                return (
                                  <div className="md:col-span-3 border-t border-girder/50 pt-3" data-testid={`dup-accounts-${d.email}`}>
                                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Duplicate account{dups.length > 1 ? "s" : ""} with this company name</p>
                                    {dups.map((o) => (
                                      <div key={o.email} className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                                        <span className="text-slate-300">{o.email} — {o.name || "no contact"}{o.activated ? " (active)" : ""}{(o.aliases || []).length ? ` · +${o.aliases.length} contact${o.aliases.length > 1 ? "s" : ""}` : ""}</span>
                                        <button onClick={(e) => { e.stopPropagation(); mergeDuplicate(d, o); }} data-testid={`merge-dup-${d.email}-${o.email}`}
                                          className="clip-corner-sm border border-red-400/60 px-2.5 py-1 font-display text-[11px] font-bold uppercase text-red-300 transition-colors hover:bg-red-400 hover:text-obsidian">
                                          Merge into this account
                                        </button>
                                      </div>
                                    ))}
                                    <p className="mt-1.5 text-xs text-slate-600">Their emails become contacts here; the duplicate account is archived and its map pin removed.</p>
                                  </div>
                                );
                              })()}
                              <div>
                                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Directory listing</p>
                                <div className="mt-1 flex items-center gap-3">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); toggleDirectory(d); }}
                                    data-testid={`directory-toggle-${d.email}`}
                                    className={`relative h-6 w-11 rounded-full transition-colors ${d.directory.visible ? "bg-hivis" : "bg-girder"}`}
                                    aria-label="Toggle directory visibility"
                                  >
                                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-obsidian transition-transform ${d.directory.visible ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                                  </button>
                                  <span className="text-sm text-slate-300">
                                    {d.directory.visible ? "Visible" : "Hidden"}
                                    {d.directory.city_state ? ` — ${d.directory.city_state}` : d.directory.listed ? "" : " (no map pin yet)"}
                                  </span>
                                </div>
                                {d.directory.needs_website && !d.directory.visible && (
                                  <p className="mt-1.5 font-mono text-[10px] uppercase tracking-widest text-amber-400/80" data-testid={`needs-website-${d.email}`}>Needs website to be listed</p>
                                )}
                                {!d.directory.listed && (
                                  <div className="mt-2" data-testid={`create-listing-block-${d.email}`}>
                                    {!d.website && (
                                      <input
                                        value={siteInput[d.email] || ""}
                                        onChange={(e) => setSiteInput({ ...siteInput, [d.email]: e.target.value })}
                                        onClick={(e) => e.stopPropagation()}
                                        placeholder="Website (https://…) — required for listing"
                                        data-testid={`listing-website-${d.email}`}
                                        className="mb-1.5 w-full border border-girder bg-obsidian px-3 py-1.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-hivis focus:outline-none"
                                      />
                                    )}
                                    <textarea
                                      value={addrInput[d.email] || ""}
                                      onChange={(e) => setAddrInput({ ...addrInput, [d.email]: e.target.value })}
                                      onClick={(e) => e.stopPropagation()}
                                      placeholder="Street address, city, state ZIP"
                                      rows={2}
                                      data-testid={`listing-address-${d.email}`}
                                      className="w-full border border-girder bg-obsidian px-3 py-1.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-hivis focus:outline-none"
                                    />
                                    <button onClick={(e) => { e.stopPropagation(); createListing(d); }} data-testid={`create-listing-${d.email}`}
                                      className="clip-corner-sm mt-1.5 border border-hivis/50 px-3 py-1.5 font-display text-[11px] font-bold uppercase text-hivis transition-colors hover:bg-hivis hover:text-obsidian">
                                      Save & list on map
                                    </button>
                                    <p className="mt-1 text-[11px] text-slate-600">Lists them right away — the dealer can still hide their own listing anytime.</p>
                                  </div>
                                )}
                              </div>
                              <div>
                                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Account names</p>
                                {editDealer === d.email ? (
                                  <div className="mt-1 space-y-2">
                                    <input value={editForm.company} onChange={(e) => setEditForm({ ...editForm, company: e.target.value })} onClick={(e) => e.stopPropagation()} placeholder="Business name" data-testid="edit-dealer-company"
                                      className="w-full border border-girder bg-obsidian px-3 py-1.5 text-sm text-slate-100 focus:border-hivis focus:outline-none" />
                                    <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} onClick={(e) => e.stopPropagation()} placeholder="Contact name" data-testid="edit-dealer-name"
                                      className="w-full border border-girder bg-obsidian px-3 py-1.5 text-sm text-slate-100 focus:border-hivis focus:outline-none" />
                                    <input value={editForm.website} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} onClick={(e) => e.stopPropagation()} placeholder="Website (https://…)" data-testid="edit-dealer-website"
                                      className="w-full border border-girder bg-obsidian px-3 py-1.5 text-sm text-slate-100 focus:border-hivis focus:outline-none" />
                                    <input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} onClick={(e) => e.stopPropagation()} placeholder="Phone" data-testid="edit-dealer-phone"
                                      className="w-full border border-girder bg-obsidian px-3 py-1.5 text-sm text-slate-100 focus:border-hivis focus:outline-none" />
                                    <select value={editForm.group} onChange={(e) => setEditForm({ ...editForm, group: e.target.value })} onClick={(e) => e.stopPropagation()} data-testid="edit-dealer-group"
                                      className="w-full border border-girder bg-obsidian px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest text-slate-300 focus:border-hivis focus:outline-none">
                                      <option value="">Franchise group…</option>
                                      {GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
                                    </select>
                                    <div className="flex gap-2">
                                      <button onClick={(e) => { e.stopPropagation(); saveDealerEdit(d); }} data-testid="edit-dealer-save"
                                        className="clip-corner-sm bg-hivis px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-obsidian transition-colors hover:bg-hivis-hover">Save</button>
                                      <button onClick={(e) => { e.stopPropagation(); setEditDealer(null); }} data-testid="edit-dealer-cancel"
                                        className="clip-corner-sm border border-girder px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-400 transition-colors hover:text-slate-200">Cancel</button>
                                    </div>
                                    <p className="text-[11px] text-slate-600">Business name + website also update the Dealer Locator pin.</p>
                                  </div>
                                ) : (
                                  <p className="mt-1 text-sm text-slate-300">
                                    {d.company || "—"} <span className="text-slate-500">/ {d.name || "—"}</span>{" "}
                                    <button onClick={(e) => { e.stopPropagation(); setEditDealer(d.email); setEditForm({ company: d.company || "", name: d.name || "", website: d.website || "", phone: d.phone || "", group: d.group || "" }); }} data-testid={`edit-dealer-${d.email}`}
                                      className="font-mono text-[10px] uppercase tracking-widest text-hivis transition-colors hover:text-hivis-hover">Edit</button>
                                  </p>
                                )}
                              </div>
                            </div>
                            {d.aliases.length > 0 && <p className="mt-3 text-xs text-slate-500">Also logs in with: {d.aliases.join(", ")}</p>}
                            <div className="mt-4 border-t border-girder/50 pt-3">
                              <button onClick={(e) => { e.stopPropagation(); deleteDealer(d); }} data-testid={`delete-dealer-${d.email}`}
                                className="font-mono text-[10px] uppercase tracking-widest text-red-400/70 transition-colors hover:text-red-400">
                                Delete this account + map pin
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    ))}
                    {filtered.length === 0 && (
                      <tr><td colSpan={7} className="px-4 py-10 text-center font-mono text-xs uppercase tracking-widest text-slate-600">No dealers match this filter</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Reveal>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-slate-600" data-testid="tracker-count">
              Showing {filtered.length} of {data.total} dealers
            </p>
          </>
        )}
      </section>
    </div>
  );
}
